/* eslint-disable max-lines */
import React from "react";

import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import {
  BOOSTERCATEGORY,
  BOOSTERNAME,
  CARENAME,
  CORENAME,
  SCALPNAME,
  SKINCATEGORY,
  SKINNAME,
} from "@/constants/names";
import type { RoundingStrategy } from "@/lib/applyPromoPricing";
import type { AtcOption, AtcProduct } from "@/types/atc";
import type { CartItem } from "@/types/cart";
import {
  ComponentData,
  ContentfulProduct,
  DpaProperties,
  IntervalDetail,
  ProductGrowthPlanData,
  Subscription,
  Update,
} from "@/types/common";
import { LayoutData } from "@/types/layoutData";
import { getMaskedId } from "@/utils/getMaskedId";

import { ThemeTypes } from "./theming";

// Type for the numerical randomizer function
const randomizer = (num: number): 0 | 1 => {
  return Math.random() > num ? 0 : 1;
};

// Type for the getOffset function which returns an object with top and left properties
const getOffset = (el: HTMLElement): { left: number; top: number } => {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY,
  };
};

// Type for the sliderAdaptiveHeightFix function
const sliderAdaptiveHeightFix = (): void => {
  try {
    if (document.querySelector(".slick-list")) {
      (document.querySelector(".slick-list") as HTMLElement).style.height = "auto";
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("sliderAdaptiveHeightFix failed ", error);
  }
};

// Type for the getArrowDirectionClicked function
const getArrowDirectionClicked = (
  prevIdx: number,
  idx: number,
  slideCount: number
): "left" | "right" => {
  if (prevIdx < idx || (prevIdx === slideCount - 1 && idx === 0)) {
    return prevIdx === 0 && idx === slideCount - 1 ? "left" : "right";
  } else {
    return "left";
  }
};

// Type for the formatWidths function
const formatWidths = (widths: string): (number | null)[] => {
  const inputWidths = widths.replace(/\s/g, "").split(",");
  const widthsArr = inputWidths.map((w) => {
    if (w === "null") {
      return null;
    } else if (w.includes("/")) {
      const split = w.split("/");
      return parseInt(split[0], 10) / parseInt(split[1], 10);
    }
    return 1;
  });
  return widthsArr;
};

// Type for the calcGrowthTime function
const calcGrowthTime = (start: string): number => {
  const d = new Date();
  const today = [
    d.getFullYear(),
    (d.getMonth() + 1).toString().padStart(2, "0"),
    d.getDate().toString().padStart(2, "0"),
  ].join("-");
  const msPerMonth = 1000 * 60 * 60 * 24 * 30;
  const utcStart = Date.UTC(
    parseInt(start.split("-")[0], 10),
    parseInt(start.split("-")[1], 10),
    parseInt(start.split("-")[2], 10)
  );
  const utcNow = Date.UTC(
    parseInt(today.split("-")[0], 10),
    parseInt(today.split("-")[1], 10),
    parseInt(today.split("-")[2], 10)
  );
  const growthTime = Math.floor((utcNow - utcStart) / msPerMonth);
  return growthTime <= 0 ? 1 : growthTime + 1;
};

// Type for the dateMapDay function
const dateMapDay = (raw: string | number): string => {
  const formatMap: { [key: string]: string } = {
    "1": "01",
    "2": "02",
    "3": "03",
    "4": "04",
    "5": "05",
    "6": "06",
    "7": "07",
    "8": "08",
    "9": "09",
    "01": "1",
    "02": "2",
    "03": "3",
    "04": "4",
    "05": "5",
    "06": "6",
    "07": "7",
    "08": "8",
    "09": "9",
  };

  const rawString = String(raw);
  return formatMap[rawString] ? formatMap[rawString] : rawString;
};

// Type for the dateMapMonth function
const dateMapMonth = (raw: string | number, short?: boolean): string => {
  let formatMap: { [key: string]: string } = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    "10": "October",
    "11": "November",
    "12": "December",
    "1": "January",
    "2": "February",
    "3": "March",
    "4": "April",
    "5": "May",
    "6": "June",
    "7": "July",
    "8": "August",
    "9": "September",
  };
  if (short) {
    formatMap = {
      "01": "Jan",
      "02": "Feb",
      "03": "Mar",
      "04": "Apr",
      "05": "May",
      "06": "Jun",
      "07": "Jul",
      "08": "Aug",
      "09": "Sept",
      "10": "Oct",
      "11": "Nov",
      "12": "Dec",
    };
  }
  const rawString = String(raw);
  return formatMap[rawString] ? formatMap[rawString] : rawString;
};

// Type for getCurrentSubscriptionFromSessionStorage
const getCurrentSubscriptionFromSessionStorage = (): string | null => {
  if (typeof window !== "undefined") {
    const currentSubscription = sessionStorage.getItem("currentSubscription");
    if (currentSubscription) {
      return currentSubscription;
    }
  }
  return null;
};

// Type for clearSubscriptionLocalStorage
const clearSubscriptionLocalStorage = (): void => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("currentSubscription");
  }
};

// Type for clearSubscriptionSessionStorage
const clearSubscriptionSessionStorage = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("currentSubscription");
  }
};

// Type for clearCartLocalStorage
const clearCartLocalStorage = (): void => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("cart");
  }
};

// Type for formatName
const formatName = (name: string): string => {
  return name && name.replace
    ? name
        .replace(/\\/g, "")
        .replace("Adaptogen", "Adapt")
        .replace("Adapt", "Adaptogen")
        .replace("One-Time Purchase", "")
        .replace(" Monthly", "")
        .replace(" Subscription", "")
        .replace(" Sub", "")
    : "";
};

// Type for formatStatus
const formatStatus = (status: string): string => {
  return status.replace("payment_failed", "Payment failed");
};

// Type for neutralCustomizeData
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const neutralCustomizeData = <T extends Record<string, any>>(obj: T, updates: Update[]): T => {
  let newObj = { ...obj };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function eachRecursive(currentObj: Record<string, any>, update: Update): Record<string, any> {
    for (const k in currentObj) {
      if (Object.prototype.hasOwnProperty.call(currentObj, k)) {
        if (typeof currentObj[k] == "object" && currentObj[k] !== null) {
          eachRecursive(currentObj[k], update);
        } else {
          if (k === update.key) {
            currentObj[k] = update.newValue;
          }
        }
      }
    }
    return currentObj;
  }

  updates.forEach((update) => {
    newObj = eachRecursive(newObj, update) as T; // Type assertion needed for recursive return
  });
  return newObj;
};

const neutralRenderComponents = (components: ComponentData[]): React.ReactElement[] => {
  return components.map((obj, i) => {
    return React.createElement(obj.comp, { key: i, data: obj.data, ...obj.custom });
  });
};

// Type for isMultipleSkuDpa
const isMultipleSkuDpa = (queryParams: string): boolean => {
  if (typeof window !== "undefined") {
    let skusParam = queryParams.split("skus=")[1];
    if (skusParam) {
      skusParam = skusParam.split("&")[0];
      if (skusParam.split(",").length > 1) {
        return true;
      }
    }
    return false;
  }
  return false;
};

const getPropertiesFromDpa = (dpaLink: string): DpaProperties | undefined => {
  if (typeof window !== "undefined") {
    const body: DpaProperties = {};

    // Use URL API to properly parse the URL and extract search parameters
    let urlParams: URLSearchParams;
    try {
      if (dpaLink.startsWith("http://") || dpaLink.startsWith("https://")) {
        const url = new URL(dpaLink);
        urlParams = url.searchParams;
      } else if (dpaLink.startsWith("?")) {
        // If dpaLink is just a query string, parse it directly
        urlParams = new URLSearchParams(dpaLink);
      } else {
        // For relative URLs, extract the query string part
        const queryIndex = dpaLink.indexOf("?");
        if (queryIndex !== -1) {
          urlParams = new URLSearchParams(dpaLink.substring(queryIndex));
        } else {
          // Fallback: try to extract query string from the old method
          const baseUrl = `${process.env.NEXT_PUBLIC_MAGENTO_URL}dpa/add/tocart`;
          const queryString = dpaLink.replace(baseUrl, "").replace(`${baseUrl}/`, "");
          urlParams = new URLSearchParams(queryString);
        }
      }
    } catch {
      // Final fallback: try to extract query string from the old method
      const baseUrl = `${process.env.NEXT_PUBLIC_MAGENTO_URL}dpa/add/tocart`;
      const queryString = dpaLink.replace(baseUrl, "").replace(`${baseUrl}/`, "");
      urlParams = new URLSearchParams(queryString);
    }
    let numProducts = 0;
    let numPurchaseSources = 0;

    if (urlParams.has("skus")) {
      const skus = urlParams.get("skus") as string;
      numProducts = skus.split(",").length;
      body.skus = skus;
    }
    if (urlParams.has("code")) {
      const code = urlParams.get("code") as string;
      body.code = code;
    }
    const maskedId = getMaskedId();
    if (maskedId) {
      body.masked_id = maskedId;
    }
    if (urlParams.has("purchase_source")) {
      let purchaseSource = urlParams.get("purchase_source") as string;
      if (purchaseSource && purchaseSource.split) {
        numPurchaseSources = purchaseSource.split(",").length;
        if (numProducts > numPurchaseSources) {
          const lastPurchaseSource = purchaseSource.split(",").at(-1) || "";
          for (let i = numPurchaseSources; i < numProducts; i++) {
            purchaseSource += `,${lastPurchaseSource}`;
          }
        }
      }
      body.purchase_source = purchaseSource;
    }
    return body;
  }
  return undefined;
};

/*
 * This is a DPA helper: return dpaKey & dpaSfx & sku  for subs only when "useDpaKey" is enabled in contentful promo data
 * use dpaSfx & sku in dpa links
 * use dpaKey & sku for ajax cart call
 * see https://nutrafol.atlassian.net/browse/NT2-26903
 */
const PROMO_KEY = "promo_product_enabled";
type DynamicBodyKey = typeof PROMO_KEY;

const getPropertiesForDpa = function (
  option: AtcOption,
  source?: string
): {
  dpaKey: DynamicBodyKey | null;
  dpaSfx: string;
  sku: string;
} {
  const sfx = option?.sku?.includes("-") ? "" : "-S01";
  const sku = option?.sku + sfx;

  //we can force some_promo_sources to trigger useDpaKey also
  if (option?.ui_data?.useDpaKey || source === "some_promo_source") {
    return {
      dpaKey: PROMO_KEY,
      dpaSfx: `&${PROMO_KEY}=true`,
      sku,
    };
  }
  return { dpaKey: null, dpaSfx: "", sku };
};
// Type for getUrlParameter
const getUrlParameter = (name: string): string => {
  if (typeof window !== "undefined") {
    name = name.replace(/[\[\]]/g, "\\$&"); // Escape special characters
    const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    const results = regex.exec(window.location.search);
    if (!results || !results[2]) {
      return "";
    }
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }
  return "";
};

// Type for getProdDeliveryParameters
const getProdDeliveryParameters = (products: {
  [key: string]: AtcProduct;
}): { productPreset: string | null; deliveryPreset: number | null } => {
  const productInUrl =
    getUrlParameter("product") && getUrlParameter("product").length > 1
      ? getUrlParameter("product")[0].toUpperCase() + getUrlParameter("product").slice(1)
      : null;
  const productPreset = productInUrl && products[productInUrl] ? productInUrl : null;
  const deliveryInUrl = getUrlParameter("delivery") || null;
  let deliveryPreset: number | null = null;

  if (productPreset && deliveryInUrl) {
    const foundIndex = products[productPreset].options.findIndex(
      (el) => el?.ui_data?.id === deliveryInUrl
    );
    deliveryPreset = foundIndex > -1 ? foundIndex : null;
  }
  return { productPreset, deliveryPreset };
};

// Type for getProductsBySub
const getProductsBySub = (subscription: Subscription): ContentfulProduct[] => {
  let product_growth_plan_data: ProductGrowthPlanData | undefined;
  try {
    product_growth_plan_data = Object.values(
      JSON.parse(subscription.product_growth_plan_data)
    )?.[0] as ProductGrowthPlanData;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Error parsing product_growth_plan_data:", e);
    return [];
  }

  let productsArray: ContentfulProduct[] = [];

  // Fixing critical errors with legacy accounts
  if (
    !!product_growth_plan_data &&
    (Array.isArray(product_growth_plan_data.child_products) ||
      (typeof product_growth_plan_data.child_products === "object" &&
        product_growth_plan_data.child_products !== null)) &&
    Object.keys(product_growth_plan_data.child_products).length > 0
  ) {
    productsArray = Object.values(product_growth_plan_data.child_products) as ContentfulProduct[];
  } else if (product_growth_plan_data) {
    productsArray = [product_growth_plan_data as ContentfulProduct];
  }

  return productsArray.sort((a) => (a?.is_core ? -1 : 1));
};

// Type for isAndroid
const isAndroid = (): boolean =>
  typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);

// Type for androidScrollFix
const androidScrollFix = (selector: string = "#Modal input[type='text']"): void => {
  try {
    if (isAndroid()) {
      document.querySelectorAll<HTMLInputElement>(selector).forEach((box) =>
        box.addEventListener("focus", () => {
          setTimeout(() => {
            box.scrollIntoView();
          }, 250);
        })
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
};

// Type for cmsFontHelper
const cmsFontHelper = (text: string | null | undefined): string => {
  return text && text.replace
    ? text
        .replace(/<em>/gm, "<span class='font-grilliItalic'>")
        .replace(/<\/em>/gm, "</span>")
        .replace(/<strong>/gm, "<span class='font-grilliBook font-bold'>")
        .replace(/<\/strong>/gm, "</span>")
        .replace(/<p>/gm, "")
        .replace(/<\/p>/gm, "")
    : text || "";
};

// Type for cmsFontHelperSup
const cmsFontHelperSup = (text: string | null | undefined): string => {
  return text && text.replace
    ? text
        .replace(/\|/gm, "<br/><br/>")
        .replace(/<em>/gm, "<sup>")
        .replace(/<\/em>/gm, "</sup>")
    : text || "";
};

// Type for cmsArrayHelper
const cmsArrayHelper = (text: string | null | undefined): string[] | undefined => {
  return text?.split(",");
};

// Type for cmsAltHelper
const cmsAltHelper = (text: string = ""): { altp: string; title: string } => {
  const arr = text && text.split ? text.split("|") : ["", ""];
  return { altp: arr[0], title: arr[1] || arr[0] };
};

// Type for getRecaptchaPublicKey
const getRecaptchaPublicKey = (): string | null => {
  if (typeof document === "undefined") {
    return null;
  }
  const script = document.getElementById("grecaptchaV3") as HTMLScriptElement;
  if (!!script) {
    const src = script.src;
    const key = src.split("?render=")[1];
    if (key) {
      return key;
    }
    return null;
  }
  return null;
};

// Type for isIpad
const isIpad = (): boolean => {
  return (
    (typeof navigator !== "undefined" &&
      navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 0) ||
    (typeof navigator !== "undefined" && !!navigator.platform.match(/iPad/i))
  );
};

/** @description Determines whether to add ? or & to the url based on whether it already has params */
const applyImgParamsToUrl = (url: string, params: Record<string, string>): string =>
  `${url}${url?.includes("?") ? "&" : "?"}${Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join("&")}`;

// Type for castPrice
const castPrice = (price: string | number): string => {
  if (typeof price !== "string") {
    price = String(price);
  }
  if (price) {
    const arr = price.replace(",", "").split(".");
    let cents = arr[1] && arr[1].length ? arr[1].slice(0, 2) : "";
    if (cents.length === 1) {
      cents = cents + "0";
    }
    const sfx = cents && cents !== "00" ? "." + cents : "";
    return `$${arr[0] + sfx}`;
  }
  return `$${price}`;
};

// Type for isElementHidden
const isElementHidden = (el: HTMLElement | null): boolean => {
  return el?.offsetParent === null;
};

// Paths that should not have a sticky ATC header
const PRODUCT_PATH_EXCLUSIONS = ["/hair/women/product/core/women/", "/hair/women/product/category"];

//As we have a generic header, make sure isProductPath matches combined isProductPath + isProductPathMen from gatsby
const isProductPath = (pathname = "") => {
  if (PRODUCT_PATH_EXCLUSIONS.some((excluded) => pathname.indexOf(excluded) > -1)) {
    return false;
  }
  return (
    pathname.indexOf("/hair/women/product/") > -1 || pathname.indexOf("/hair/men/product/") > -1
  );
};

// Type for isElementInViewport
const isElementInViewport = (el: HTMLElement): boolean => {
  const rect = el.getBoundingClientRect();

  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
    rect.top < (window.innerHeight || document.documentElement.clientHeight)
  );
};

// Type for isMobile
const isMobile: boolean = typeof document !== "undefined" && document.body.clientWidth < 768;

const intervals: { [key: number]: IntervalDetail } = {
  0: {
    text: "One-Time Purchase",
    // icon: <IconOnce />,
  },
  1: {
    text: "Monthly Subscription",
    // icon: <IconRecurring />,
  },
  3: {
    text: "3-Month Subscription",
    // icon: <IconRecurring />,
  },
  6: {
    text: "6-Month Subscription",
    // icon: <IconRecurring />,
  },
};

type FreqByIntervalReturn = [number, string, React.ElementType | undefined];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getFreqByInterval = (interval: string | any[] | undefined): FreqByIntervalReturn => {
  const countOfMonth = parseInt(String(interval?.[0]), 10) || 0;
  return [countOfMonth, intervals[countOfMonth].text, intervals[countOfMonth].icon];
};

// Type for getFormattedDate
const getFormattedDate = (date: string | null | undefined): string => {
  const isoDateString = date?.replace?.(/ /, "T");
  const inputDate = new Date(isoDateString || "");
  if (!(inputDate instanceof Date) || isNaN(inputDate.getTime())) {
    return "Invalid Date";
  }

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  return new Intl.DateTimeFormat("en-US", options).format(inputDate);
};

// Type for addDays
const addDays = (date: Date, days: number): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + days,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
};

// Type for pauseAllVideos
const pauseAllVideos = (): void => {
  const medias = [...document.querySelectorAll<HTMLVideoElement>("video:not(.no-pause)")];
  medias.forEach(function (media) {
    try {
      media?.pause();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  });
};

// Type for isNoIndexNoFollow
const isNoIndexNoFollow = (layoutData: LayoutData | undefined): boolean =>
  !!layoutData?.no_index_no_follow;

const noIndexDoFollow: string[] = [];
const isNoIndexDoFollow = (slug: string): boolean => noIndexDoFollow.includes(slug);

// Define `playing` property on HTMLMediaElement prototype
if (
  typeof HTMLMediaElement !== "undefined" &&
  !Object.prototype.hasOwnProperty.call(HTMLMediaElement.prototype, "playing")
) {
  Object.defineProperty(HTMLMediaElement.prototype, "playing", {
    get: function (this: HTMLMediaElement) {
      return !!(this.readyState > 2 && this.currentTime > 0 && !this.paused && !this.ended);
    },
  });
}

const isEmailValid = (email: string): boolean => {
  return Boolean(
    String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
  );
};

// Type for filterContentfulProductsSortByIsCore
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isSubCasted = (product: { is_subscription: string | number }): boolean => {
  return parseInt(String(product?.is_subscription)) === 1;
};

const filterContentfulProductsSortByIsCore = (product: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  child_products: { [key: string]: any };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const growthProducts: any[] = [];
  if (product && product.child_products) {
    Object.keys(product.child_products).forEach((prodKey) => {
      const ContentfulProduct = product.child_products[prodKey];
      if (!isSubCasted(ContentfulProduct)) {
        growthProducts.push(ContentfulProduct);
      }
    });
  }
  growthProducts.sort((a, b) => (a.is_core < b.is_core ? 1 : -1));
  return growthProducts;
};

const PRODMAP: { [key: string]: string } = {
  "ADV1-S01": "Hair Growth Nutraceutical for women",
  "BAL1-S01": "Women's Balance Hair Growth Nutraceutical",
  "POS1-S01": "Postpartum Hair Growth Nutraceutical",
  "RPURIFIER-N01": "Shampoo",
  "SDEFENDER-N01": "Conditioner",
  "GACTIVATOR-N01": "Hair Serum",
  "BBLOCKER-N01": "Scalp Mask",
  "HBALANCEKIT-N01": "Scalp Travel Set",
  "WOMFHK3-S01": "Fullest Hair Kit",
  "MENPRO1-S01": "Hair Growth Nutraceutical for men",
  "MENFUL1-S01": "Full Support",
};

const formatPrice = (val: number, rounding?: RoundingStrategy): string => {
  /*
   * Contentful Promo option example
   * {
   * “code”: “VALENTINESGWP”,
   * “coef”: 0.9,
   * “rounding”: “floor”,
   * “savings”: “Save 10%”
   * },
   */
  if (rounding && typeof Math[rounding] === "function") {
    return String(Math[rounding](val));
  }
  return val % 1 > 0 ? val.toFixed(2) : String(val);
};

const formatPriceWithCurrency = (amount: number | string, currency: string = "$"): string => {
  if (typeof amount === "string") {
    // If it's already formatted with currency, return as-is
    if (amount.startsWith(currency)) return amount;
    // Strip thousands separators (e.g. "1,155.00") before parsing
    const normalized = amount.replace(/,/g, "");
    const numAmount = parseFloat(normalized);
    if (isNaN(numAmount)) return amount;
    const rounded = Math.round(numAmount);
    const formatted = numAmount === rounded ? String(rounded) : numAmount.toFixed(2);
    return `${currency}${formatted}`;
  }
  const rounded = Math.round(amount);
  const formatted = amount === rounded ? String(rounded) : amount.toFixed(2);
  return `${currency}${formatted}`;
};

const mapIntervalToDisplayText = (interval: string, singular: boolean = false) => {
  switch (interval) {
    case "1 month":
      return singular ? "Month" : "Month";
    case "3 month":
      return singular ? "3 Month" : "3 Months";
    case "6 month":
      return singular ? "6 Month" : "6 Months";
  }
};

const formatPriceWithInterval = (
  amount: number | string,
  interval: string,
  currency: string = "$"
): string => {
  const formattedAmount = formatPriceWithCurrency(amount, currency);
  if (!interval) {
    return formattedAmount;
  }
  return `${formattedAmount} / ${mapIntervalToDisplayText(interval)}`;
};

const getPrice = (option: AtcOption) => {
  if (!option.bundled_price) {
    return option.old_price;
  }
  return option.bundled_price % 1 > 0
    ? option.bundled_price.toFixed(2)
    : String(option.bundled_price);
};

const getOldPrice = (option: AtcOption) => {
  const old_price = option.price_range?.minimum_price?.regular_price?.value || option.old_price;

  if (old_price) {
    return old_price % 1 > 0 ? old_price.toFixed(2) : old_price;
  }

  return null;
};

const smsCopy: string = `By checking this box, you agree to receive recurring automated promotional and personalized marketing text messages (e.g., cart reminders) from Nutrafol at the cell number used when signing up. Consent to receive automated marketing texts is not a condition of any purchase. Messages may be sent using an automatic telephone dialing system or other automated system for the selection or dialing of numbers. Reply HELP for help and STOP to cancel. Msg frequency varies. Msg & data rates may apply. View <a href="/our/brand/support/legal/terms-of-use/#sms-terms" target="_blank" rel="noopener noreferrer">Terms</a> & <a href="/our/brand/support/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy</a>.`;

const replaceNextRun = (subscription: Subscription): string | undefined => {
  return subscription?.next_run?.replace(/ /, "T");
};

// Type for classNames utility
type ClassValue =
  string | { [key: string]: boolean | undefined | null } | ClassValue[] | undefined | null;

// Type for getGender
const getGender = (subscription: Subscription): string | undefined => {
  try {
    const parsedData = JSON.parse(subscription?.product_growth_plan_data || "{}");
    const firstValue = Object.values(parsedData)?.[0] as ProductGrowthPlanData;
    return firstValue?.core_category;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("getGender error", e);
    return undefined;
  }
};

// Type for generateScopeClass
const generateScopeClass = (input: string = ""): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  const base36 = (hash >>> 0).toString(36);
  return `nss-${base36}`;
};

// Type for cn utility (re-defined from original file, kept for completeness)
const cn = (...inputs: ClassValue[]): string => {
  const classNamesResult = clsx(inputs);
  return twMerge(classNamesResult);
};

/**
 ** sanitizeForDisplay gets rid of common control characters and different types of whitespace that might not be obvious visually.
 */
const sanitizeForDisplay = (text = "") => {
  const regex = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\u00A0\u200B-\u200D\uFEFF\x0A\x0D]/g;

  return text?.replace ? text.replace(regex, "") : text;
};

/**
 * Removes backslash-escaped quotes from API text (e.g. "Women\'s" -> "Women's").
 */
const unescapeProductDisplayText = (text = ""): string => {
  if (typeof text !== "string") return String(text);
  return text.replace(/\\'/g, "'").replace(/\\"/g, '"');
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
};

const scrollToId = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const elementPosition = element.offsetTop;
    const offsetPosition = elementPosition - 50;
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Check if a sku is a one-time purchase
const isOneTimePurchase = (sku: string): boolean | undefined => {
  if (!sku || typeof sku !== "string") {
    return undefined;
  }

  const skuPattern = /-([A-Za-z0-9]{3})$/;
  const match = sku.match(skuPattern);

  if (!match) {
    return undefined;
  }

  // Check if the last 3 characters are "N01" (case insensitive)
  const lastThreeChars = match[1];
  return lastThreeChars.toUpperCase() === "N01";
};

// For use in styling the header throughout the checkout flow
const getThemeFromOrderItems = (items: { product_sku: string }[]): ThemeTypes => {
  if (!items || items.length === 0) {
    return "master";
  }

  // Skin-only orders keep master theming; cart/order confirmation still uses SKU logic.
  const exclusivelyMenOrder = items.every((item) => item?.product_sku?.includes("MEN"));
  if (exclusivelyMenOrder) {
    return "men";
  }

  // Default to master theme
  return "master";
};

const has3PackUpgrade = (product: CartItem) => {
  return !!product?.three_pack_sku && product?.subscription_type === "Monthly";
};

const getFormattedProductCategory = (productCategory: string): string => {
  switch (productCategory) {
    case SKINCATEGORY:
      return SKINNAME;
    case BOOSTERCATEGORY:
      return BOOSTERNAME;
    case SCALPNAME:
      return CARENAME;
    default:
      return CORENAME;
  }
};

export {
  androidScrollFix,
  smsCopy,
  randomizer,
  getOffset,
  formatWidths,
  calcGrowthTime,
  dateMapDay,
  dateMapMonth,
  getFormattedDate,
  addDays,
  getCurrentSubscriptionFromSessionStorage,
  clearSubscriptionLocalStorage,
  clearSubscriptionSessionStorage,
  clearCartLocalStorage,
  formatName,
  formatStatus,
  sliderAdaptiveHeightFix,
  getArrowDirectionClicked,
  neutralCustomizeData,
  neutralRenderComponents,
  isMultipleSkuDpa,
  getPropertiesFromDpa,
  getPropertiesForDpa,
  getUrlParameter,
  getProductsBySub,
  getProdDeliveryParameters,
  getRecaptchaPublicKey,
  cmsFontHelper,
  cmsFontHelperSup,
  cmsArrayHelper,
  cmsAltHelper,
  isIpad,
  castPrice,
  isElementHidden,
  isProductPath,
  isAndroid,
  isElementInViewport,
  isMobile,
  isNoIndexNoFollow,
  isNoIndexDoFollow,
  isSubCasted,
  pauseAllVideos,
  formatPrice,
  formatPriceWithCurrency,
  mapIntervalToDisplayText,
  formatPriceWithInterval,
  PRODMAP,
  isEmailValid,
  replaceNextRun,
  getFreqByInterval,
  filterContentfulProductsSortByIsCore,
  getGender,
  generateScopeClass,
  cn,
  getPrice,
  getOldPrice,
  sanitizeForDisplay,
  unescapeProductDisplayText,
  scrollToTop,
  debounce,
  has3PackUpgrade,
  isOneTimePurchase,
  getThemeFromOrderItems,
  getFormattedProductCategory,
  applyImgParamsToUrl,
  scrollToId,
};
